import { AppState, AppThunkDispatch } from '../..';
import { enqueueSnackbarAction } from '../../snackbar/actions';
import RebootConfig from '../../../services/reboot-config';
import { getCommandsByDeviceKey } from '../actions';
import ShortCommand from '../../../services/short-commands';
import PingConfig from '../../../services/ping-config';
import ShortMessageRequestConfiguration from '../../../services/short-message-request-configuration';
import SaberRekey from '../../../services/saber-rekey';
import TransferOverrideConfigurations from '../../../services/transfer-override-configurations';
import MeterValueAdjustmentConfig from '../../../services/meter-value-adjustment-config';
import { ShortCommandPostRequest, WakeUpByDeviceIdRequest, PushConfigFilesRequestBody, GetFirmwareStatusRequest } from '../../../services/types';
import { RebootRequestBody } from '../../../entities/reboot-commands-v2/models';
import { AssetDeviceConfigRequest } from '../../../entities/asset-device-config-v2/models';
import AssetDeviceConfiguration from '../../../services/asset-device-configuration';
import { PingRequest } from '../../../entities/ping-commands-v2/models';
import { ShortMessagePostRequest } from '../../../entities/short-message-request-configuration-v1/models';
import { TransferOverridePostRequest } from '../../../entities/transfer-override-configurations-v1/models';
import { SaberRekeyPayload } from '../../../entities/saber-rekey-v1/models';
import { MeterValueAdjustmentRequest } from '../../../entities/meter-value-adjustment-config/models';
import CarrierManagement from '../../../services/carrier-management';
import DeviceWakeUps from '../../../services/device-wakeups-v2';
import { WakeupRequest } from '../../../entities/device-wake-ups-v2/wakeup-request';
import LocatorCommands from '../../../services/locator-commands';
import {
  fileToBase64,
} from '../../../globals/utils';
import ConfigurationFiles from '../../../services/config-files';
import GeoFenceConfiguration from '../../../services/geo-fence-configuration';
import { GeoFenceRemoveRequest } from '../../../entities/geofenceConfigurations-v1/geoFence-delete-request';
import RemoteFlash from '../../../services/remote-flash';

const commandQueuedSuccessfully = (dispatch: Function) =>
  dispatch(
    enqueueSnackbarAction({
      message: 'Command queued successfully',
      options: { variant: 'success' },
    }),
  );

const commandQueueUnsuccessful = (dispatch: Function) =>
  dispatch(
    enqueueSnackbarAction({
      message: 'Command queue unsuccessful',
      options: { variant: 'error' },
    }),
  );
const commandlessDeviceTypes = new Set(['PL143']);
export const waitForSuccessfulQueueThenMessage =
  (transactionId: string | undefined, deviceKey: string) => async (dispatch: AppThunkDispatch) => {
    const successfullyQueued = await dispatch(
      getCommandsByDeviceKey({ deviceKey, statuses: '1' }, false, transactionId),
    );
    if (successfullyQueued) return commandQueuedSuccessfully(dispatch);
    return commandQueueUnsuccessful(dispatch);
  };

export const wakeupDevicesAction =
  (input: WakeUpByDeviceIdRequest) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const wakeupService = new CarrierManagement(client, envVariables.carrierManagementV1BaseUrl);
      try {
        const response = await wakeupService.wakeUpByDeviceId(input);
        if (response.status === 200) {
          dispatch(
            enqueueSnackbarAction({
              message: 'Wakeup operation successful',
              options: {
                variant: 'success',
              },
            }),
          );
        } else {
          dispatch(
            enqueueSnackbarAction({
              message: 'Wakeup operation failed',
              options: {
                variant: 'error',
              },
            }),
          );
        }
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Wakeup operation failed',
            options: {
              variant: 'error',
            },
          }),
        );
      }
    };

export const wakeupDevicesV2Action =
  (input: WakeupRequest) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const wakeupService = new DeviceWakeUps(client, envVariables.deviceWakeUpsV2);
    try {
      const response = await wakeupService.wakeupDevices(input, input.wakeupType === 'wifi');
      if (response.status === 200) {
        const deviceType = input?.devices[0].deviceType;
        const displayMessage = response?.data?.devices && response?.data?.devices[0]?.description.startsWith("Success");
        dispatch(
          enqueueSnackbarAction({
            message: deviceType.startsWith("PL143") && !displayMessage ? response?.data?.devices[0]?.description : 'Wake Up operation successful',
            options: {
              variant: displayMessage ? 'success' : 'warning',
            },
          }),
        );
      } else {
        dispatch(
          enqueueSnackbarAction({
            message: 'Wake Up operation failed',
            options: {
              variant: 'error',
            },
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Wake Up operation failed',
          options: {
            variant: 'error',
          },
        }),
      );
    }
  };

export const requestCheckinWakeupAction =
  (input: WakeupRequest) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const requestCheckinService = new DeviceWakeUps(client, envVariables.deviceWakeUpsV2);
    try {
      const response = await requestCheckinService.requestCheckin(input, false);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Request Checkin operation successful',
            options: {
              variant: 'success',
            },
          }),
        );
      } else {
        dispatch(
          enqueueSnackbarAction({
            message: 'Request Checkin operation operation failed',
            options: {
              variant: 'error',
            },
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Request Checkin operation operation failed',
          options: {
            variant: 'error',
          },
        }),
      );
    }
  };

export const deviceRebootAction =
  (input: RebootRequestBody, deviceKey: any) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const rebootConfig = new RebootConfig(client, envVariables.rebootConfigV2BaseUrl);
      try {
        const response = await rebootConfig.rebootCommandForDevice(input);
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Command queue updating slower than expected',
            options: { variant: 'warning' },
          }),
        );
      }
    };

export const shortCommandAction =
  (input: ShortCommandPostRequest, deviceKey: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const shortCommand = new ShortCommand(client, envVariables.shortCommandsV1BaseUrl);
      try {
        const response = await shortCommand.queueShortCommandForAssetOrDevice(input);
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const queueAssetDeviceConfigurationAction =
  (input: AssetDeviceConfigRequest, deviceKey: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const assetDeviceConfiguration = new AssetDeviceConfiguration(
        client,
        envVariables.assetDeviceConfigV2BaseUrl,
      );
      try {
        const response = await assetDeviceConfiguration.queueAssetDeviceConfiguration(input);
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const pingCommandsAction =
  (input: PingRequest, deviceKey: string, deviceType?: string | null) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();

      const rebootPingConfig = new PingConfig(
        client,
        envVariables.pingCommandsV2BaseUrl,
      );

      try {
        const response = await rebootPingConfig.pingCommandToDevice(input);
        const transactionId = response.data?.configs[0].transactionId;
        if (deviceType && commandlessDeviceTypes.has(deviceType)) {
          if (response) {
            if (response.status === 200) {
              commandQueuedSuccessfully(dispatch);
            } else {
              commandQueueUnsuccessful(dispatch);
            }
          }
        } else {
          dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
        }
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const shortMessageRequestConfigAction =
  (input: ShortMessagePostRequest, deviceKey: any, classificationType?: any) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();

      const shortMessageRequestConfiguration = new ShortMessageRequestConfiguration(
        client,
        envVariables.shortMessageRequestConfigurationV1BaseUrl,
      );
      try {
        const response = await shortMessageRequestConfiguration.queueShortMessageForAssetOrDevice(
          input,
          classificationType,
        );
        const transactionId = response.data?.configs[0].transactionId;

        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const saberRekeyAction =
  (input: SaberRekeyPayload, deviceKey: any) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const saberRekeyService = new SaberRekey(client, envVariables.saberRekeyV1BaseUrl);
      try {
        const response = await saberRekeyService.queueSaberRekeyForADevice(input);
        const { transactionId } = response.data;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const transferOverrideConfigAction =
  (input: TransferOverridePostRequest, deviceKey: any) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const transferOverrideConfigService = new TransferOverrideConfigurations(
        client,
        envVariables.transferOverrideConfigurationsV1BaseUrl,
      );
      try {
        const response = await transferOverrideConfigService.queueConfigsToOverrideFileTransfer(
          input,
        );
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const getRemoteFlashStatus =
  (input: GetFirmwareStatusRequest) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      try {
        const remoteFlash = new RemoteFlash(client, envVariables.remoteFlashV1BaseUrl);
        const response = await remoteFlash.getFirmwareStatus(input);
        if (response.status === 200) {
          dispatch(
            enqueueSnackbarAction({
              message: `Get Flash Status Request ${response.data.firmware?.flashStatus}`,
              options: { variant: 'success' },
            }),
          );
          return true;
        }
        dispatch(
          enqueueSnackbarAction({
            message: `Get Flash Status Request Failed`,
            options: { variant: 'error' },
          }),
        );
        return false;
      }
      catch (error) {
        dispatch(
          enqueueSnackbarAction({
            message: `Failed to retrive Flash Status`,
            options: { variant: 'error' },
          }),
        );
        return false;
      }
    };

export const meterValueAdjustmentConfigAction =
  (input: MeterValueAdjustmentRequest, deviceKey: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const meterValueAdjustmentConfigService = new MeterValueAdjustmentConfig(
        client,
        envVariables.meterValueConfigurationV2BaseUrl,
      );
      try {
        const response = await meterValueAdjustmentConfigService.meterValueAdjustmentByDevice(input);
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: `Command queuing failed: ${error.toString()}`,
            options: { variant: 'error' },
          }),
        );
      }
    };

export const geoFenceDeleteConfigAction =
  (input: GeoFenceRemoveRequest, deviceKey: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const geoFenceConfigService = new GeoFenceConfiguration(
        client,
        envVariables.geoFenceConfigBaseUrl,
      );
      try {
        const response = await geoFenceConfigService.removeConfigs(input);
        const transactionId = response.data?.configs[0].transactionId;
        dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
      } catch (error: any) {
        const { configs } = error.response.data;
        if (configs && configs.length > 0) {
          dispatch(
            enqueueSnackbarAction({
              message: `Command queuing failed: ${configs[0].description}`,
              options: { variant: 'error' },
            }),
          );
        } else {
          dispatch(
            enqueueSnackbarAction({
              message: `Command queuing failed: ${error.toString()}`,
              options: { variant: 'error' },
            }),
          );
        }
      }
    };

export const factoryResetAction =
  (parameters: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    try {
      const locatorCommand = new LocatorCommands(client, envVariables.locatorCommandURL);
      const response = await locatorCommand.factoryReset(parameters);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message: `Success`,
            options: { key: (Date.now() + Math.random()).toString(), variant: 'success' },
          }),
        );
        return true;
      }
      dispatch(
        enqueueSnackbarAction({
          message: response.statusText,
          options: { key: (Date.now() + Math.random()).toString(), variant: 'warning' },
        }),
      );

      return false;
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: `Command Queuing Failed`,
          options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
        }),
      );
      return false;
    }
  };
export const queueCommands =
  (parameters: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    try {
      const locatorCommand = new LocatorCommands(client, envVariables.locatorCommandURL);
      const response = await locatorCommand.queueCommands(parameters);
      if (response.status === 200) {
        dispatch(
          enqueueSnackbarAction({
            message: `Success`,
            options: { key: (Date.now() + Math.random()).toString(), variant: 'success' },
          }),
        );
      } else {
        dispatch(
          enqueueSnackbarAction({
            message: response.statusText,
            options: { key: (Date.now() + Math.random()).toString(), variant: 'warning' },
          }),
        );
      }
      return response;
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: `Command Queuing Failed`,
          options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
        }),
      );
      return null;
    }
  };

export const handleSendFileToDevice =
  (file: File, fileTypeId: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        search: { selectedEntity },
        login: {
          client,
          envVariables: { configurationFilesV1BaseUrl },
        },
      } = getState();
      const deviceKey = selectedEntity?.deviceKey;
      const deviceId = selectedEntity?.deviceId;
      const deviceType = selectedEntity?.deviceType;
      const file64: string | unknown = file && (await fileToBase64(file));
      if (deviceKey && deviceId && deviceType && typeof file64 === 'string') {
        const configFile = file64.replace(/(^[^,]*,)/g, '');
        const input: PushConfigFilesRequestBody = {
          configs: [
            {
              deviceId,
              deviceType,
              configFile: {
                fileTypeId,
                configFile,
              },
            },
          ]
        };
        const commands = new ConfigurationFiles(client, configurationFilesV1BaseUrl);
        try {
          const response = await commands.pushConfigFiles(input);
          const { description, transactionId } = response.data.configs[0];
          dispatch(waitForSuccessfulQueueThenMessage(transactionId, deviceKey));
          return description;
        } catch (error: any) {
          try {
            const { configs }: any = error.response.data;
            dispatch(
              enqueueSnackbarAction({
                message: configs[0].description,
                options: {
                  variant: 'error',
                },
              }),
            );
          } catch (error) {
            dispatch(
              enqueueSnackbarAction({
                message: 'Request Failure',
                options: {
                  variant: 'error',
                },
              }),
            );
          }
          return null;
        }
      }
      return null;
    };